import React, { useState, useEffect } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { getAPIRequest } from 'infrastructure/api'
import {
  makeKiddomDeleteRequest,
  makeKiddomPostRequest,
  makeKiddomPutRequest,
} from 'infrastructure/api'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'

const SAMLForm = ({
  districtID,
  schoolID,
}: {
  districtID: number
  schoolID: number
}) => {
  const doSetupRequest = useWithErrorNotification(
    { errorTitle: 'Error occured. Please try again.' },
    (requestBody: object) => {
      return makeKiddomPostRequest<{ error_code?: unknown }>({
        kiddomPath: `/v1/sis/ingestion/setupsaml`,
        requestBody,
      })
    },
  )
  const doUpdateRequest = useWithErrorNotification(
    { errorTitle: 'Error occured. Please try again.' },
    (requestBody: object) => {
      return makeKiddomPutRequest<{ error_code?: unknown }>({
        kiddomPath: `/v1/sis/ingestion/updatesaml`,
        requestBody,
      })
    },
  )
  const deleteRequest = useWithErrorNotification(
    { errorTitle: 'Error Deleting' },
    makeKiddomDeleteRequest,
  )
  const [metadata, setMetadata] = useState('')
  const [attributename, setAttributeName] = useState('mail')
  const [onerrorurl, setonErrorURL] = useState('')
  const [err, setErr] = useState('')
  const [feedback, setMsg] = useState('')
  const [isupdate, setUpdate] = useState(false)
  const [regenerateCert, setRegen] = useState(false)
  const metadataOnChange = (e: any) => {
    setErr('')
    setMetadata(e.target.value)
  }
  const attributeNameOnChange = (e: any) => {
    setErr('')
    setAttributeName(e.target.value)
  }
  const onErrorURLOnChange = (e: any) => {
    setErr('')
    setonErrorURL(e.target.value)
  }
  const onRegenCertChange = () => {
    setErr('')
    setRegen(!regenerateCert)
  }
  const setSAMLData = (resp: any) => {
    if (resp.error == null) {
      setonErrorURL(resp.on_error_url)
      setAttributeName(resp.attribute_name)
      setMetadata(resp.metadata)
      setRegen(false)
      setUpdate(true)
    }
  }
  async function loadData() {
    let url
    if (schoolID === -1) {
      url = `/v1/sis/ingestion/saml?district_id=${districtID}`
    } else {
      url = `/v1/sis/ingestion/saml?school_id=${schoolID}`
    }
    try {
      const res = await getAPIRequest(url)
      setSAMLData(res)
    } catch {
      setErr('Unable to load data. Please Try again')
    }
  }
  function tempMessage(msg: string) {
    setMsg(msg)
    setTimeout(() => {
      setMsg('')
    }, 3000)
  }
  function resetData() {
    setonErrorURL('')
    setAttributeName('mail')
    setMetadata('')
    setRegen(false)
    setUpdate(false)
  }
  useEffect(() => {
    const getData = async () => {
      let url
      if (schoolID === -1) {
        url = `/v1/sis/ingestion/saml?district_id=${districtID}`
      } else {
        url = `/v1/sis/ingestion/saml?school_id=${schoolID}`
      }
      try {
        const res = await getAPIRequest(url)
        setSAMLData(res)
      } catch {}
    }
    districtID && schoolID && getData()
  }, [districtID, schoolID])
  const submit = async () => {
    let body
    if (schoolID === -1) {
      body = {
        on_error_url: onerrorurl,
        attribute_name: attributename,
        metadata,
        district_id: districtID,
      }
    } else {
      body = {
        on_error_url: onerrorurl,
        attribute_name: attributename,
        metadata,
        school_id: schoolID,
      }
    }
    let response
    if (isupdate) {
      try {
        response = await doUpdateRequest(body)
      } catch {
        setErr('Error occured. Please try again.')
      }
    } else {
      try {
        response = await doSetupRequest(body)
      } catch {
        setErr('Error occured. Please try again.')
      }
    }

    if (response && response.error_code === undefined) {
      if (isupdate) {
        tempMessage('Update was successful')
      } else {
        tempMessage('SAML setup was successful')
      }
      loadData()
    } else if (response === undefined || response.error_code) {
      setErr('Error occured. Please try again.')
    }
  }
  const callDelete = async () => {
    let kiddomPath
    if (schoolID === -1) {
      kiddomPath = `/v1/sis/ingestion/deletesaml?district_id=${districtID}`
    } else {
      kiddomPath = `/v1/sis/ingestion/deletesaml?school_id=${schoolID}`
    }
    try {
      await deleteRequest({ kiddomPath })

      resetData()
    } catch {
      setErr('Error occured. Unable to delete')
    }
    tempMessage('Delete successful')
  }
  const marginBottom = { marginBottom: 15 }
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {isupdate ? <span>Update SAML</span> : <span>Setup SAML</span>}
      </AccordionSummary>
      <AccordionDetails>
        <FormControl style={{ minWidth: 350 }}>
          <TextField
            value={onerrorurl}
            label="On Error Redirect URL"
            variant="outlined"
            onChange={onErrorURLOnChange}
            size="small"
            style={marginBottom}
            inputProps={{
              'aria-label': 'On Error Redirect URL',
            }}
          />
          <TextField
            value={attributename}
            label="Attribute Name"
            variant="outlined"
            onChange={attributeNameOnChange}
            size="small"
            style={marginBottom}
            inputProps={{
              'aria-label': 'Attribute Name',
            }}
          />
          <TextField
            value={metadata}
            label="Metadata or MetadataURL"
            variant="outlined"
            onChange={metadataOnChange}
            size="medium"
            style={marginBottom}
            inputProps={{
              'aria-label': 'Metadata or MetadataURL',
            }}
          />
          {err && (
            <Alert severity="error" style={marginBottom}>
              {err}
            </Alert>
          )}
          {feedback && (
            <Alert severity="success" style={marginBottom}>
              {feedback}
            </Alert>
          )}
          {isupdate && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={regenerateCert}
                  onChange={() => onRegenCertChange()}
                />
              }
              label="Regenerate Certificate"
            />
          )}
          <Button onClick={submit} variant="outlined" color="secondary">
            {isupdate ? <span>Update</span> : <span>Create</span>}
          </Button>
          {isupdate && (
            <Button onClick={callDelete} variant="outlined" color="secondary">
              Delete
            </Button>
          )}
        </FormControl>
      </AccordionDetails>
    </Accordion>
  )
}

export default SAMLForm
